import * as FullStory from '@fullstory/browser';

function getEnvironment() {
  const host = window.location.host;
  if (host.includes('sdk.katapult.com')) return 'prod';
  if (host.includes('sandbox')) return 'sandbox';
  return 'dev';
}

// In-store session state. `instoreVars` are the segment properties; `identifiedAsStore` is true once the session
// carries the store identity; `phoneIdentified` is true once the customer's phone has taken over as the identity.
let instoreVars = null;
let identifiedAsStore = false;
let phoneIdentified = false;

export const getInstoreVccFullStoryVars = () => instoreVars;

// Called when the phone identity is sent. Fullstory ignores a second identify, so the store identity is cleared
// first. Once the phone is the identity, later calls (the uid arriving after verification) only update the
// properties. Returns true when the in-store flow was active, so the caller knows the call was handled.
export function handOverToPhoneIdentity(phone, phoneVars) {
  if (!instoreVars) {
    return false;
  }
  const vars = { ...phoneVars, phone_str: phone };
  if (phoneIdentified) {
    FullStory.setUserVars(vars);
    return true;
  }
  if (identifiedAsStore) {
    FullStory.anonymize();
    identifiedAsStore = false;
  }
  FullStory.identify(phone, { ...vars, ...instoreVars });
  phoneIdentified = true;
  return true;
}

const STORE_KEY = 'fsInstoreVccStoreId';

function getIdentifiedStore() {
  try {
    return sessionStorage.getItem(STORE_KEY);
  } catch (e) {
    return null;
  }
}

function rememberIdentifiedStore(storeId) {
  try {
    sessionStorage.setItem(STORE_KEY, storeId);
  } catch (e) {
    // Storage can be blocked; the only cost is one extra anonymize if the store changes later.
  }
}

// Tags the Fullstory session as an in-store VCC session so a segment can filter on flow_name_str = INSTORE_VCC.
// retailer_id_str and store_id_str are the optional identifiers for narrowing by retailer or store.
// Only a plain numeric id may become the Fullstory identity or be written to sessionStorage.
const isValidStoreId = value => /^\d{1,19}$/.test(String(value ?? ''));

function fullStoryInstoreVccInit({ retailerId, retailerName, storeId: rawStoreId, storeUsername }, attempt = 0) {
  const storeId = isValidStoreId(rawStoreId) ? rawStoreId : null;
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000;

  try {
    const userVars = {
      displayName: storeUsername || 'unknown',
      partner_name_str: retailerName || 'unknown',
      flow_name_str: 'INSTORE_VCC',
      retailer_id_str: retailerId ? String(retailerId) : 'unknown',
      store_id_str: storeId ? String(storeId) : 'unknown',
      environment_str: getEnvironment(),
    };
    const { displayName, ...segmentVars } = userVars;
    instoreVars = segmentVars;
    if (phoneIdentified) {
      // The customer already verified their phone; keep that identity and only refresh the properties.
      FullStory.setUserVars(segmentVars);
    } else if (storeId) {
      const uid = String(storeId);
      // Fullstory keeps the first identity of a session and ignores a later identify with another uid, so a
      // browser that already scanned another store would stay on that store. Reset the identity on a change.
      const previousUid = getIdentifiedStore();
      if (previousUid && previousUid !== uid) {
        FullStory.anonymize();
      }
      FullStory.identify(uid, { ...userVars, displayName: `${retailerName || 'Store'} #${storeId}` });
      rememberIdentifiedStore(uid);
      identifiedAsStore = true;
    } else {
      FullStory.setUserVars(userVars);
    }
  } catch (err) {
    if (attempt < MAX_RETRIES) {
      setTimeout(
        () => fullStoryInstoreVccInit({ retailerId, retailerName, storeId: rawStoreId, storeUsername }, attempt + 1),
        RETRY_DELAY_MS * 2 ** attempt,
      );
    } else {
      // eslint-disable-next-line no-console
      console.error('FullStory not loaded', err);
    }
  }
}

export default fullStoryInstoreVccInit;
